import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useProfile } from "../context/ProfileContext";
import SortableTh from "../components/SortableTh";
import {
  FaSpinner,
  FaUsers,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaUserTie,
  FaEye,
  FaEdit,
  FaTrash,
  FaIdCard,
  FaFilter,
  FaCalendar,
  FaHistory,
} from "react-icons/fa";
import ActivityLogsModal from "../components/ActivityLogsModal";

const toLocalDateString = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const NewEmployees = () => {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const isSuperAdmin = profile?.email === "superadmin@udrivo.in" || profile?.role?.slug === "superadmin";

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filters, setFilters] = useState({
    employeeName: "",
    city: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [activityLogsModal, setActivityLogsModal] = useState({
    show: false,
    module: "Employee",
    entityId: null,
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const sortConfigRef = useRef({ key: null, direction: "asc" });

  const employeeSortThClass =
    "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 bg-gray-50";

  const fetchEmployees = async (page = 1, overrides = null) => {
    try {
      setLoading(true);
      const useStart = overrides?.startDate !== undefined ? overrides.startDate : startDate;
      const useEnd = overrides?.endDate !== undefined ? overrides.endDate : endDate;
      const useFilters = overrides?.filters !== undefined ? overrides.filters : filters;
      const params = new URLSearchParams();
      params.append("page", String(page));
      params.append("limit", String(pagination.limit));
      params.append("employeeType", "new");
      if (useStart) params.append("startDate", toLocalDateString(useStart));
      if (useEnd) params.append("endDate", toLocalDateString(useEnd));
      if (useFilters.employeeName?.trim()) params.append("employeeName", useFilters.employeeName.trim());
      if (useFilters.city?.trim()) params.append("city", useFilters.city.trim());
      if (sortConfigRef.current?.key) {
        params.append("sortBy", sortConfigRef.current.key);
        params.append("sortOrder", sortConfigRef.current.direction || "desc");
      }
      const url = `${API.ADMIN.EMPLOYEES_LIST}?${params}`;
      const response = await axiosInstance.get(url);
      const data = response.data.employees || [];
      setEmployees(data);
      if (response.data.pagination) {
        const pg = response.data.pagination;
        setPagination((prev) => ({
          ...prev,
          page: pg.page ?? page,
          total: pg.total ?? 0,
          totalPages: pg.totalPages ?? 1,
          hasNextPage: pg.hasNextPage ?? false,
          hasPrevPage: pg.hasPrevPage ?? false,
          limit: pg.limit ?? prev.limit,
        }));
      }
    } catch (error) {
      console.error("Error fetching new employees:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!profile) return;
    fetchEmployees(pagination.page);
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilter = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchEmployees(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setPagination((prev) => ({ ...prev, page: newPage }));
    fetchEmployees(newPage);
  };

  const handleSortSet = (key, direction) => {
    const next = { key, direction };
    setSortConfig(next);
    sortConfigRef.current = next;
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchEmployees(1);
  };

  const handleSortLabelClick = (key) => {
    setSortConfig((prev) => {
      const next = {
        key,
        direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
      };
      sortConfigRef.current = next;
      setPagination((p) => ({ ...p, page: 1 }));
      fetchEmployees(1);
      return next;
    });
  };

  const employeeSortTh = (columnKey, label) => (
    <SortableTh
      columnKey={columnKey}
      label={label}
      activeKey={sortConfig.key}
      activeDirection={sortConfig.direction}
      onSortSet={handleSortSet}
      onSortLabelClick={handleSortLabelClick}
      className={employeeSortThClass}
      theme="green"
    />
  );

  const handleDelete = async (employeeId) => {
    if (!window.confirm("Are you sure you want to delete this employee?")) return;
    try {
      await axiosInstance.delete(`${API.ADMIN.EMPLOYEE_DELETE}/${employeeId}`);
      setEmployees((prev) => prev.filter((e) => e._id !== employeeId));
      fetchEmployees(pagination.page);
    } catch (error) {
      console.error("Error deleting employee:", error);
    }
  };

  const handleToggleEmployeeActive = async (employee) => {
    const nextActive = !(employee.isActive !== false);
    try {
      await axiosInstance.put(API.ADMIN.EMPLOYEE_NEW_UPDATE(employee._id), { isActive: nextActive });
      setEmployees((prev) => prev.map((e) => (e._id === employee._id ? { ...e, isActive: nextActive } : e)));
    } catch (error) {
      console.error("Error updating employee active status:", error);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8 bg-gray-100 min-h-screen">
        <div className="flex flex-col justify-center items-center py-20">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <FaUsers className="text-6xl text-blue-500 mb-4 mx-auto" />
            <FaSpinner className="animate-spin text-3xl text-blue-500 mb-4 mx-auto" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading New Employees</h2>
            <p className="text-gray-600 mb-4">Please wait...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8 bg-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-gray-800">New Employees</h2>
        {isSuperAdmin && (
          <button
            onClick={() => navigate("/employees/new-add")}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow-md"
          >
            + Add New Employee
          </button>
        )}
      </div>

      {profile && (
        <>
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl shadow-lg mb-6 p-6 border border-purple-100">
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 flex-wrap">
              <div className="flex-1 min-w-[140px]">
                <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <FaCalendar className="mr-2 text-purple-500" /> Start Date
                </label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  dateFormat="dd/MM/yyyy"
                  showYearDropdown
                  showMonthDropdown
                  placeholderText="Select start date"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg shadow-sm bg-white"
                  maxDate={new Date()}
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <FaCalendar className="mr-2 text-purple-500" /> End Date
                </label>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  dateFormat="dd/MM/yyyy"
                  showYearDropdown
                  showMonthDropdown
                  placeholderText="Select end date"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg shadow-sm bg-white"
                  maxDate={new Date()}
                  minDate={startDate}
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="text-sm font-medium text-gray-700 mb-2">Employee Name</label>
                <input
                  type="text"
                  value={filters.employeeName}
                  onChange={(e) => setFilters((f) => ({ ...f, employeeName: e.target.value }))}
                  placeholder="Search by name"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm"
                />
              </div>
              <div className="flex-1 min-w-[120px]">
                <label className="text-sm font-medium text-gray-700 mb-2">City</label>
                <input
                  type="text"
                  value={filters.city}
                  onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))}
                  placeholder="City"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm"
                />
              </div>
              <button
                className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-3 rounded-lg shadow-md hover:shadow-lg flex items-center font-medium"
                onClick={handleFilter}
              >
                <FaFilter className="mr-2" /> Apply Filter
              </button>
              <button
                type="button"
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                  setFilters({ employeeName: "", city: "" });
                  setPagination((prev) => ({ ...prev, page: 1 }));
                  fetchEmployees(1, { startDate: null, endDate: null, filters: { employeeName: "", city: "" } });
                }}
                className="px-6 py-3 rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
              >
                Clear
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            {employees.length === 0 ? (
              <div className="text-center py-12">
                <FaUsers className="text-gray-400 text-4xl mx-auto mb-4" />
                <p className="text-gray-500">No new employees found</p>
              </div>
            ) : (
              <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
                <table className="min-w-full">
                  <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm">
                    <tr>
                      {employeeSortTh("fullName", "Employee Details")}
                      {employeeSortTh("contact", "Contact Information")}
                      {employeeSortTh("role", "Role")}
                      {employeeSortTh("status", "Status")}
                      <th className={employeeSortThClass}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {employees.map((employee) => (
                      <tr key={employee._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="flex items-center mb-1">
                              <FaUser className="text-blue-500 mr-2" />
                              <span className="text-sm font-medium text-gray-900">{employee.fullName}</span>
                            </div>
                            <p className="text-sm text-gray-500 flex items-center">
                              <FaIdCard className="mr-1" /> ID: {employee.employeeCode}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <div className="flex items-center">
                              <FaPhone className="text-green-500 mr-2" />
                              <span className="text-sm text-gray-900">{employee.phone}</span>
                            </div>
                            <div className="flex items-center">
                              <FaEnvelope className="text-purple-500 mr-2" />
                              <span className="text-sm text-gray-600">{employee.emailId}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {/* <div className="flex items-center">
                            <FaMapMarkerAlt className="text-red-500 mr-2" />
                            <span className="text-sm text-gray-600">{employee.city || "—"}</span>
                          </div> */}
                          <div className="flex items-center mt-1">
                            <FaUserTie className="text-indigo-500 mr-2" />
                            <span className="text-sm text-gray-600">{employee.role}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${employee.isActive !== false ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-700"}`}>
                            {employee.isActive !== false ? "Active" : "Inactive"}
                          </span>
                          {isSuperAdmin && (
                            <button
                              type="button"
                              onClick={() => handleToggleEmployeeActive(employee)}
                              className="ml-2 text-sm text-blue-600 hover:text-blue-800"
                            >
                              {employee.isActive !== false ? "Set Inactive" : "Set Active"}
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            <Link
                              to={`/employees/profile/${employee._id}`}
                              className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50"
                              title="View Profile"
                            >
                              <FaEye />
                            </Link>
                            {employee.hasActivityLogs && (
                              <button
                                type="button"
                                onClick={() =>
                                  setActivityLogsModal({
                                    show: true,
                                    module: "Employee",
                                    entityId: employee._id,
                                  })
                                }
                                className="text-indigo-600 hover:text-indigo-800 p-2 rounded-lg hover:bg-indigo-50 inline-flex items-center text-sm"
                                title="View Activity Logs"
                              >
                                <FaHistory />
                              </button>
                            )}
                            {isSuperAdmin && (
                              <>
                                <Link
                                  to={`/employees/new/edit/${employee._id}`}
                                  className="text-green-600 hover:text-green-800 p-2 rounded-lg hover:bg-green-50"
                                  title="Edit"
                                >
                                  <FaEdit />
                                </Link>
                                <button
                                  onClick={() => handleDelete(employee._id)}
                                  className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50"
                                  title="Delete"
                                >
                                  <FaTrash />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {employees.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} employee{pagination.total !== 1 ? "s" : ""} total)
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPrevPage}
                    className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="px-2 text-sm text-gray-600">{pagination.page} / {pagination.totalPages}</span>
                  <button
                    type="button"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNextPage}
                    className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
          <ActivityLogsModal
            show={activityLogsModal.show}
            module={activityLogsModal.module}
            entityId={activityLogsModal.entityId}
            onClose={() =>
              setActivityLogsModal({ show: false, module: "Employee", entityId: null })
            }
          />
        </>
      )}
    </div>
  );
};

export default NewEmployees;
